/**
 * Assessment Instance Service
 * PRD: docs/PRD_Audit_Lifecycle_v2.md Section 6.2
 * 
 * Manages assessment_instances collection — scoped evaluations linked to audits.
 * Calculates scores based on selected themes only (scope filtering).
 */

import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { auditServiceQMS } from './auditService_qms';

const COLLECTION = 'assessment_instances';

export class AssessmentInstanceService {
  constructor() {
    this.collectionRef = collection(db, COLLECTION);
  }

  /**
   * Create new assessment instance
   * @param {string} auditId
   * @param {string} instrumentId
   * @param {array} selectedThemeIds
   * @param {string} organizationId
   * @param {object} organizationalScope
   * @param {string} userId
   * @returns {Promise<string>} instanceId
   */
  async create(auditId, instrumentId, selectedThemeIds, organizationId, organizationalScope, userId) {
    if (!auditId) throw new Error('auditId is required');
    if (!instrumentId) throw new Error('instrumentId is required');
    if (!selectedThemeIds || selectedThemeIds.length === 0) {
      throw new Error('selectedThemeIds is required (min 1)');
    }
    if (!organizationId) throw new Error('organizationId is required');

    const instanceId = `instance_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const now = Timestamp.now();

    const payload = {
      id: instanceId,
      organizationId,
      instrumentId,
      instrumentName: '',
      
      // Context
      source: 'audit',
      auditId,
      
      // Scope
      selectedThemeIds,
      organizationalScope: organizationalScope || { nodeId: 'hospital', nodeLabel: 'Spital', nodeType: 'hospital' },
      
      // Answers
      answers: {},
      
      // Result
      result: null,
      
      // Status
      status: 'pending',
      completedAt: null,
      
      // Metadata
      createdAt: now,
      createdBy: userId
    };

    const docRef = doc(this.collectionRef, instanceId);
    await setDoc(docRef, payload);

    return instanceId;
  }

  /**
   * Get instance by ID
   * @param {string} instanceId
   * @returns {Promise<object>}
   */
  async getById(instanceId) {
    if (!instanceId) throw new Error('instanceId is required');

    const ref = doc(this.collectionRef, instanceId);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      throw new Error('Assessment instance not found');
    }

    const data = snap.data();
    return {
      id: snap.id,
      ...data,
      completedAt: data.completedAt?.toDate?.() ?? data.completedAt,
      createdAt: data.createdAt?.toDate?.() ?? data.createdAt
    };
  }

  /**
   * Delete assessment instance document (after removal from audit.assessmentInstances).
   * @param {string} instanceId
   */
  async deleteInstance(instanceId) {
    if (!instanceId) throw new Error('instanceId is required');
    const ref = doc(this.collectionRef, instanceId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    await deleteDoc(ref);
  }

  /**
   * Save answer to instance
   * @param {string} instanceId
   * @param {string} questionId
   * @param {string} value - 'DA' | 'NU' | 'N/A'
   * @param {string} userId
   */
  /**
   * Save scored / single-select answer (HHSAF, PSA, etc.)
   * @param {string} instanceId
   * @param {string} questionId
   * @param {string} value - option value from instrument question
   * @param {string} userId
   */
  async saveScoredAnswer(instanceId, questionId, value, userId) {
    if (!instanceId) throw new Error('instanceId is required');
    if (!questionId) throw new Error('questionId is required');
    if (value == null || value === '') {
      throw new Error('value is required');
    }

    const ref = doc(this.collectionRef, instanceId);
    const current = await getDoc(ref);
    if (!current.exists()) throw new Error('Assessment instance not found');

    const data = current.data();
    if (data.status === 'completed') {
      throw new Error('Cannot modify completed assessment instance');
    }

    const answerKey = `answers.${questionId}`;
    await updateDoc(ref, {
      [answerKey]: {
        value: String(value),
        answeredAt: Timestamp.now(),
        answeredBy: userId
      },
      status: 'in_progress'
    });
  }

  async saveAnswer(instanceId, questionId, value, userId) {
    if (!instanceId) throw new Error('instanceId is required');
    if (!questionId) throw new Error('questionId is required');
    if (!['DA', 'NU', 'N/A'].includes(value)) {
      throw new Error('value must be DA, NU, or N/A');
    }

    const ref = doc(this.collectionRef, instanceId);
    const current = await getDoc(ref);
    if (!current.exists()) throw new Error('Assessment instance not found');

    const data = current.data();
    if (data.status === 'completed') {
      throw new Error('Cannot modify completed assessment instance');
    }

    const answerKey = `answers.${questionId}`;
    await updateDoc(ref, {
      [answerKey]: {
        value,
        answeredAt: Timestamp.now(),
        answeredBy: userId
      },
      status: 'in_progress'
    });
  }

  /**
   * Complete assessment instance and calculate scoped score
   * @param {string} instanceId
   * @param {object} instrument - full instrument document from assessment_instruments
   * @param {array} selectedThemeIds
   * @returns {Promise<object>} resultSummary
   */
  async complete(instanceId, instrument, selectedThemeIds) {
    if (!instanceId) throw new Error('instanceId is required');
    if (!instrument) throw new Error('instrument is required');
    if (!selectedThemeIds || selectedThemeIds.length === 0) {
      throw new Error('selectedThemeIds is required');
    }

    const ref = doc(this.collectionRef, instanceId);
    const current = await getDoc(ref);
    if (!current.exists()) throw new Error('Assessment instance not found');

    const data = current.data();
    const answers = data.answers || {};

    // Calculate scoped score
    const resultSummary = this.calculateScopedScore(answers, instrument, selectedThemeIds);

    return this._finalizeComplete(ref, data, instanceId, resultSummary);
  }

  /**
   * Complete a scored assessment instance (HHSAF, PSA).
   */
  async completeScored(instanceId, instrument, selectedThemeIds) {
    if (!instanceId) throw new Error('instanceId is required');
    if (!instrument) throw new Error('instrument is required');
    if (!selectedThemeIds || selectedThemeIds.length === 0) {
      throw new Error('selectedThemeIds is required');
    }

    const ref = doc(this.collectionRef, instanceId);
    const current = await getDoc(ref);
    if (!current.exists()) throw new Error('Assessment instance not found');

    const data = current.data();
    const answers = data.answers || {};
    const resultSummary = this.calculateScopedScoredScore(
      answers,
      instrument,
      selectedThemeIds
    );

    return this._finalizeComplete(ref, data, instanceId, resultSummary);
  }

  async _finalizeComplete(ref, data, instanceId, resultSummary) {
    const now = Timestamp.now();
    await updateDoc(ref, {
      result: resultSummary,
      status: 'completed',
      completedAt: now
    });

    await auditServiceQMS.updateAssessmentInstance(data.auditId, instanceId, {
      status: 'completed',
      completedAt: now,
      resultSummary
    });

    return resultSummary;
  }

  /**
   * Calculate scoped score based on selected themes
   * PRD Section 6.2 — exact implementation
   * @param {object} answers - { questionId: { value, answeredAt, answeredBy } }
   * @param {object} instrument - full instrument document
   * @param {array} selectedThemeIds - theme IDs in scope
   * @returns {object} result summary
   */
  calculateScopedScore(answers, instrument, selectedThemeIds) {
    // Get all questions from selected themes
    const scopedQuestions = (instrument.components || [])
      .filter((comp) => selectedThemeIds.includes(comp.id))
      .flatMap((comp) => comp.questions || []);

    // Filter to answered questions (exclude N/A from denominator)
    const answered = scopedQuestions.filter((q) => {
      const ans = answers[q.id];
      return ans && ans.value !== 'N/A';
    });

    // Count positive responses
    const positive = answered.filter((q) => answers[q.id]?.value === 'DA');
    const negative = answered.filter((q) => answers[q.id]?.value === 'NU');

    const scopeScore = positive.length;
    const scopeTotal = answered.length;
    const scorePercent = scopeTotal > 0 ? Math.round((scopeScore / scopeTotal) * 100) : 0;
    const gapCount = negative.length;

    // Calculate by-theme breakdown
    const byTheme = selectedThemeIds.map((themeId) => {
      const theme = instrument.components.find((c) => c.id === themeId);
      if (!theme) return null;

      const themeQuestions = theme.questions || [];
      const themeAnswered = themeQuestions.filter((q) => {
        const ans = answers[q.id];
        return ans && ans.value !== 'N/A';
      });
      const themePositive = themeAnswered.filter((q) => answers[q.id]?.value === 'DA');
      const themeNegative = themeAnswered.filter((q) => answers[q.id]?.value === 'NU');

      return {
        themeId: theme.id,
        themeLabel: theme.nameRo || theme.name || theme.id,
        score: themePositive.length,
        total: themeAnswered.length,
        percent: themeAnswered.length > 0 ? Math.round((themePositive.length / themeAnswered.length) * 100) : 0,
        gapQuestionIds: themeNegative.map((q) => q.id)
      };
    }).filter(Boolean);

    return {
      scopeScore,
      scopeTotal,
      scorePercent,
      byTheme,
      gapCount,
      scoringMode: 'checklist'
    };
  }

  /**
   * Scoped score for scored instruments (option.score per question).
   */
  calculateScopedScoredScore(answers, instrument, selectedThemeIds) {
    const scopedComponents = (instrument.components || []).filter((comp) =>
      selectedThemeIds.includes(comp.id)
    );

    let scopeScore = 0;
    let scopeTotal = 0;

    const byTheme = scopedComponents.map((theme) => {
      let themeScore = 0;
      let themeMax = 0;
      const themeQuestions = theme.questions || [];

      for (const q of themeQuestions) {
        const ans = answers[q.id];
        if (!ans) continue;
        const val = ans.value ?? ans;
        const opt = (q.options || []).find((o) => o.value === val);
        const qMax = Math.max(...(q.options || []).map((o) => o.score || 0), 0);
        themeScore += opt?.score ?? 0;
        themeMax += qMax;
      }

      scopeScore += themeScore;
      scopeTotal += themeMax;

      return {
        themeId: theme.id,
        themeLabel: theme.nameRo || theme.name || theme.id,
        score: themeScore,
        total: themeMax,
        percent: themeMax > 0 ? Math.round((themeScore / themeMax) * 100) : 0,
        gapQuestionIds: []
      };
    });

    const scorePercent =
      scopeTotal > 0 ? Math.round((scopeScore / scopeTotal) * 100) : 0;

    return {
      scopeScore,
      scopeTotal,
      scorePercent,
      byTheme,
      gapCount: 0,
      scoringMode: 'scored'
    };
  }

  /**
   * Get all instances for an audit
   * @param {string} auditId
   * @returns {Promise<array>}
   */
  async getByAudit(auditId) {
    if (!auditId) return [];
    
    const q = query(this.collectionRef, where('auditId', '==', auditId));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        ...data,
        completedAt: data.completedAt?.toDate?.() ?? data.completedAt,
        createdAt: data.createdAt?.toDate?.() ?? data.createdAt
      };
    });
  }
}

export const assessmentInstanceService = new AssessmentInstanceService();
